import React, { useState, useEffect } from "react";
import { Pie } from "react-chartjs-2";
import { Paper, Typography, Grid, CircularProgress, Card } from "@mui/material";

const TotalBalanceChart = () => {
  const paperStyle = {
    boxShadow: "none",
    padding: "1rem",
    borderRadius: "18px",
    minHeight: "100%",
    width: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    flexGrow: 1,
    overflow: "hidden",
    wordWrap: "break-word",
  };

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedLabels, setSelectedLabels] = useState([]);

  const uType = sessionStorage.getItem("uType");

  const colors = {
    "Channel Partner": "#092E1F",
    "Super Distributor": "#8F00FF",
    "Master Distributor": "#B25AEF",
    "Area Distributor": "#FF7B31",
    "Retailer": "#00DDE5",
  };

  const fetchData = async () => {
    const token = sessionStorage.getItem("jwtToken");
    try {
      const response = await fetch(
        "https://api.ipaisa.co.in/myapp/auth/hierarchy-wallet-count",
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch data");
      }

      const result = await response.json();
      setData(result.body);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  if (loading) {
    return (
      <Paper style={{ boxShadow: "none", padding: "1rem", borderRadius: "18px", textAlign: "center" }}>
        <CircularProgress />
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper style={{ boxShadow: "none", padding: "1rem", borderRadius: "18px", textAlign: "center", color: "red" }}>
        Error: {error}
      </Paper>
    );
  }

  const uTypeFilters = {
    ADMIN: ["Channel Partner", "Super Distributor", "Master Distributor", "Area Distributor", "Retailer"],
    CHANNELPARTNER: ["Super Distributor", "Master Distributor", "Area Distributor", "Retailer"],
    SUPERDISTRIBUTOR: ["Master Distributor", "Area Distributor", "Retailer"],
    MASTERDISTRIBUTOR: ["Area Distributor", "Retailer"],
    AREADISTRIBUTOR: ["Retailer"],
  };

  const allowedLabels = uTypeFilters[uType] || [];

  const rawData = {
    "Channel Partner": parseFloat(data?.channelpartnerWalletCount || 0),
    "Super Distributor": parseFloat(data?.superdistributorWalletCount || 0),
    "Master Distributor": parseFloat(data?.masterdistributorWalletCount || 0),
    "Area Distributor": parseFloat(data?.areadistributorWalletCount || 0),
    "Retailer": parseFloat(data?.retailerWalletCount || 0),
  };

  const filteredLabels = selectedLabels.length > 0 ? selectedLabels : allowedLabels;
  const filteredData = filteredLabels.map((label) => rawData[label]);
  const filteredColors = filteredLabels.map((label) => colors[label]);

  const totalWalletBalance = filteredData.reduce((sum, value) => sum + value, 0);

  const chartData = {
    labels: filteredLabels,
    datasets: [
      {
        data: filteredData,
        backgroundColor: filteredColors,
        hoverBackgroundColor: filteredColors,
      },
    ],
  };

  const handleLabelClick = (label) => {
    setSelectedLabels((prev) =>
      prev.includes(label)
        ? prev.filter((item) => item !== label)
        : [...prev, label]
    );
  };

  return (
    <Paper style={paperStyle}>
      <Grid container direction="column">
        <Typography style={{ fontWeight: "bold", fontSize: "16px", wordBreak: "break-word", overflow: "hidden" }}>
          Total Balance
        </Typography>

        <Grid container spacing={2} style={{ marginTop: "1%" }}>
          <Grid item xs={12} md={7}>
            <div style={{ position: "relative", height: "100%", width: "100%" }}>
              <Pie data={chartData} options={{ responsive: true, maintainAspectRatio: false }} height={300} />
            </div>
          </Grid>

          <Grid item xs={12} md={5} container direction="column" justifyContent="center">
            {/* Total Wallet Balance Card */}
            <Grid item xs={5}>
              <Card
                style={{
                  backgroundColor: "#007bff",
                  color: "white",
                  padding: "10px",
                  textAlign: "center",
                  borderRadius: "10px",
                  fontWeight: "bold",
                  marginBottom: "10px",
                }}
              >
                <Typography sx={{ fontSize: '16px' }}>Total Wallet Balance</Typography>
                <Typography sx={{ fontSize: '28px' }}>₹ {totalWalletBalance.toFixed(2)}</Typography>
              </Card>
            </Grid>

            {allowedLabels.map((label) => (
              <Grid
                key={label}
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: "10px",
                  cursor: "pointer",
                  opacity: selectedLabels.length > 0 && !selectedLabels.includes(label) ? 0.5 : 1,
                }}
                onClick={() => handleLabelClick(label)}
              >
                <div
                  style={{
                    backgroundColor: colors[label],
                    borderRadius: "50%",
                    width: "10px",
                    height: "10px",
                    marginRight: "10px",
                    visibility: selectedLabels.length > 0 && !selectedLabels.includes(label) ? "hidden" : "visible",
                  }}
                ></div>
                <Typography>
                  {label} <span style={{ color: "gray", fontSize: "12px" }}> : ₹{rawData[label].toFixed(2)}</span>
                </Typography>
              </Grid>
            ))}
          </Grid>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default TotalBalanceChart;
